import cloneDeep from 'lodash.clonedeep';
import { take, put, call, all, select, takeLatest, fork, delay } from 'redux-saga/effects';
import { sendErrorToSentry } from 'JS/logging';
import preloadScribeFonts from 'JS/shared/helpers/preloadScribeFonts';

import {
  CREATE_SCRIBE_FROM_TEMPLATE,
  createScribeFromTemplateSuccess,
  GET_SCRIBE_TEMPLATES,
  getScribeTemplatesFailed,
  getScribeTemplatesByCategorySuccess,
  SAVE_SCRIBE_AS_TEMPLATE,
  saveScribeAsTemplateSuccess,
  saveScribeAsTemplateFailed,
  CONTENTFUL_OAUTH_SUCCESS,
  contentfulOauthInvalid,
  contentfulOauthLoaded,
  createScribeFromTemplateFailed
} from '../actionCreators/templatesActions';
import { showCallout, showError } from '../actionCreators/uiActions';
import { addScribe, ADD_SCRIBE_SUCCESS } from '../actionCreators/scribeActions';
import {
  getTemplatesByCategory,
  getSingleTemplate,
  entryToTemplate,
  getAssets
} from '../shared/providers/TemplatesContentProvider';
import {
  getContentfulEnvironment,
  saveTemplate,
  saveImage,
  saveTemplateAudio
} from '../shared/providers/TemplatesManagementProvider';
import { appServices } from '../shared/helpers/app-services/AppServices';
import * as tokenHelper from '../shared/helpers/ContentfulHelper';

import { getScribeById, getContentfulManagementToken } from './selectors';

function* getScribeTemplatesByCategory() {
  try {
    const { error, categories } = yield call(getTemplatesByCategory);
    if (!error) {
      yield put(getScribeTemplatesByCategorySuccess(categories));
    }
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError({ message: resx('error-load-templates-failed') }));
    yield put(getScribeTemplatesFailed(error));
  }
}

function* saveTemplateAssets(template) {
  const images = template.elements.filter(element => element.image);
  try {
    // UPLOAD IMAGES TO CONTENTFUL
    yield call(throttleRequests, { array: images, handleRequests: uploadImagesToContentful });

    // UPLOAD AUDIO TO CONTENTFUL
    if (template.audioClips.length !== 0) {
      for (const clip of template.audioClips) {
        const contentfulAudioAsset = yield call(saveTemplateAudio, clip);
        clip.contentfulId = contentfulAudioAsset.sys.id;
        clip._audioUrl = null;
      }
    }
  } catch (error) {
    throw error;
  }
}

function* uploadImagesToContentful(images, batch) {
  const contentfulImagesAssets = yield all(images.map(image => saveImage(image)));
  contentfulImagesAssets.forEach(({ originalImage, contentfulAsset }) => {
    originalImage.contentfulId = contentfulAsset.sys.id;
    originalImage._imageUrl = null;
  });
  return batch;
}

function* throttleRequests({ requestPerSecond = 3, array = [], handleRequests = () => {} }) {
  const length = array.length;
  /**
   * batchSuccessfullyCompleted
   * Can be used in case of any error creating the teh Template to delete the assets that were already uploaded
   */
  const batchSuccessfullyCompleted = [];
  for (let i = 0; i < length; i += requestPerSecond) {
    const batchCompleted = yield call(handleRequests, array.slice(i, i + requestPerSecond), i);
    batchSuccessfullyCompleted.push(batchCompleted);
    yield delay(1000);
  }
  yield delay(1500);
}

async function getTemplateThumbnailBuffer(template) {
  const response = await window.fetch(template.thumbnailImage);
  const buffer = await response.arrayBuffer();
  return buffer;
}

function* saveScribeAsTemplate(action) {
  const scribe = yield select(getScribeById, action.scribeId);
  const template = cloneDeep(scribe);
  try {
    yield call(saveTemplateAssets, template);
    const thumbnailBuffer = yield call(getTemplateThumbnailBuffer, template);
    const entry = yield call(saveTemplate, template, thumbnailBuffer);
    yield put(saveScribeAsTemplateSuccess());
    yield put(
      showCallout({
        title: 'Project saved as a template',
        details: `Template id: ${entry.sys.id}`
      })
    );
  } catch (error) {
    console.error(error);
    let description = error.message;
    yield put(saveScribeAsTemplateFailed(error));
    if (error.name === 'AccessTokenInvalid') {
      yield put(contentfulOauthInvalid());
      description = 'Access Token Invalid. Try Reauthorizing with Contentful.';
    }
    yield put(
      showError({
        message: 'Could not save project as a template',
        description
      })
    );
  }
}

function* getAllTemplateAssetsFromContentful(templateClone) {
  const scribe = cloneDeep(templateClone);
  const images = scribe.elements.filter(el => el.type === 'Image');
  const audioClips = scribe.audioClips?.length ? scribe.audioClips : [scribe.audio].filter(Boolean);
  const contentfulIds = [...images, ...audioClips].map(asset => asset.contentfulId);
  const { assets, error } = yield call(getAssets, contentfulIds);
  if (assets && !error) {
    yield put({ type: 'TEMPLATE_ASSETS_FETCHED_FROM_CONTENTFUL', assets });
    return assets;
  }
}

async function downloadAllAssetFilesFromContentful(assets) {
  return await Promise.all(
    assets.map(async asset => {
      const response = await window.fetch(asset.fields.file.url);
      const file = await response.blob();

      return {
        contentfulId: asset.sys.id,
        file
      };
    })
  );
}

async function createAllUserAssets(assetFiles) {
  const userAssets = [];
  for (const assetFile of assetFiles) {
    const userAssetId = await appServices.uploadAsset(assetFile.file);
    userAssets.push({
      ...assetFile,
      userAssetId
    });
  }
  return userAssets;
}

/**
 * This creates a blank Scribe without any element in it.
 * Needed as we have to create the Scribe first in the DB
 * before cloning the `template` elements.
 */
function* createScribeFromTemplate({ templateId, replaceHistory = false }) {
  const templateFromContentul = yield call(getSingleTemplate, templateId);
  const templateClone = entryToTemplate(templateFromContentul);

  if (templateClone) {
    try {
      const assets = yield call(getAllTemplateAssetsFromContentful, templateClone);
      const assetFiles = yield call(downloadAllAssetFilesFromContentful, assets);
      const userAssets = yield call(createAllUserAssets, assetFiles);

      const imageElements = templateClone.elements.filter(el => el.type === 'Image');
      const audioClips = templateClone.audioClips?.length
        ? templateClone.audioClips
        : [templateClone.audio].filter(Boolean);

      imageElements.forEach(el => {
        const userAsset = userAssets.find(ua => ua.contentfulId === el.contentfulId);
        el.image.assetId = userAsset.userAssetId;
      });
      audioClips.forEach(clip => {
        const userAsset = userAssets.find(ua => ua.contentfulId === clip.contentfulId);
        clip.assetId = userAsset.userAssetId;
        clip.source = 'template';
        clip.fileName = 'Template audio';
        clip.type = clip.audioType || 'voiceover';
        clip.volume = clip.volume || 1;
        delete clip._audioUrl;
      });

      yield call(preloadScribeFonts, templateClone);
      yield put(
        addScribe({
          title: templateClone.title,
          canvasSize: templateClone.canvasSize,
          templateId,
          replaceHistory,
          source: {
            type: 'template',
            meta: {
              templateId,
              title: templateClone.title
            }
          },
          elements: templateClone.elements,
          thumbnail: templateClone.thumbnailImage,
          settings: templateClone.settings,
          cursor: templateClone.cursor,
          scenes: templateClone.scenes,
          audioClips
        })
      );
      yield take(ADD_SCRIBE_SUCCESS);
      yield put(createScribeFromTemplateSuccess());
    } catch (error) {
      yield put(createScribeFromTemplateFailed());
      sendErrorToSentry(error);
    }
  }
}

function* contentfulOauthSuccess(action) {
  const accessToken = action.payload;
  yield call(getContentfulEnvironment, accessToken);
  yield call(tokenHelper.setToken, accessToken);
}

function* initialSaga() {
  let accessToken = yield select(getContentfulManagementToken);
  if (!accessToken) {
    accessToken = yield call(tokenHelper.getToken);
    if (accessToken) {
      yield put(contentfulOauthLoaded(accessToken));
    }
  }
  if (accessToken) {
    try {
      yield call(getContentfulEnvironment, accessToken);
    } catch (error) {
      console.error(error);
      if (error.name === 'AccessTokenInvalid') {
        yield put(contentfulOauthInvalid());
        yield call(tokenHelper.removeToken);
      }
    }
  }
}

// `takeLeading` is available in newer version of redux-saga but this is the low-level effects
// which are the same - as taken from https://redux-saga.js.org/docs/api/#:~:text=fetchUser)%0A%7D-,Notes,-takeLeading
const takeLeading = (patternOrChannel, saga, ...args) =>
  fork(function*() {
    while (true) {
      const action = yield take(patternOrChannel);
      yield call(saga, ...args.concat(action));
    }
  });

export default function* templatesSaga() {
  yield fork(initialSaga);
  yield takeLeading(CREATE_SCRIBE_FROM_TEMPLATE, createScribeFromTemplate);
  yield takeLatest(GET_SCRIBE_TEMPLATES, getScribeTemplatesByCategory);
  yield takeLatest(SAVE_SCRIBE_AS_TEMPLATE, saveScribeAsTemplate);
  yield takeLatest(CONTENTFUL_OAUTH_SUCCESS, contentfulOauthSuccess);
}
